import { useState } from 'react';
import type { FormEvent } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { forgotPassword as apiForgotPassword } from '../api/auth.ts';
import { localizeApiError } from '../api/errorMessages.ts';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { Button, FormField, Input } from '../ui/index.ts';
import { AUTH_LINK_CLASS, AuthPageFrame } from './auth/AuthPageFrame.tsx';

/**
 * Forgot-password request — Field Notes rebuild (epic #950 PR-2,
 * formerly the console-shared `createForgotPassword` factory
 * instantiation).
 *
 * Wire behavior preserved: `POST /v1/auth/password/forgot` always lands
 * on the same in-place success card (no account-enumeration oracle).
 * Errors render inline only.
 */
export function ForgotPassword(): JSX.Element {
  const { fetchImpl } = useSession();
  const t = useT();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const trimmedEmail = email.trim();
    if (trimmedEmail.length === 0) {
      setError(t('forgot.error.empty_email'));
      return;
    }
    setSubmitting(true);
    try {
      await apiForgotPassword({ email: trimmedEmail }, fetchImpl);
      setSubmittedEmail(trimmedEmail);
      setEmail('');
    } catch (err) {
      setError(localizeApiError(err, t));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthPageFrame
      title={t('forgot.subtitle')}
      description={t('forgot.description')}
      footer={
        <Link to="/login" data-testid="forgot-back-login" className={AUTH_LINK_CLASS}>
          {t('common.back_to_login')}
        </Link>
      }
    >
      {submittedEmail !== null ? (
        <div
          className="flex flex-col gap-3 rounded-lg border border-ok-line bg-raised p-5"
          data-testid="forgot-success"
        >
          <h2 className="m-0 flex items-center gap-2 text-heading text-ink">
            <CheckCircle2 aria-hidden="true" className="h-4 w-4 shrink-0 text-ok" />
            {t('forgot.success.title')}
          </h2>
          <p className="m-0 text-body text-ink-mid">
            {t('forgot.success.body', { email: submittedEmail })}
          </p>
          <div className="mt-1">
            <Link to="/login" data-testid="forgot-login-link" className={AUTH_LINK_CLASS}>
              {t('common.back_to_login')}
            </Link>
          </div>
        </div>
      ) : (
        <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
          <FormField label={t('forgot.email')} fieldId="forgot-email">
            <Input
              id="forgot-email"
              type="email"
              mono
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              disabled={submitting}
              required
              data-testid="forgot-email"
            />
          </FormField>
          {error !== null ? (
            <p className="m-0 text-label text-bad" role="alert" data-testid="forgot-error">
              {error}
            </p>
          ) : null}
          <Button
            type="submit"
            variant="primary"
            loading={submitting}
            data-testid="forgot-submit"
            className="mt-1 h-9 w-full"
          >
            {submitting ? t('forgot.submitting') : t('forgot.submit')}
          </Button>
        </form>
      )}
    </AuthPageFrame>
  );
}
